import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { ErrorPage } from "pages/ErrorPage";
import { NewVersionModal } from "pages/NewVersionModal";
import {
  isChunkMissingError,
  isThirdPartyDomMutationError,
} from "utils/errors";
import { IssueReporter } from "utils/issueReporter";

type ErrorCatchProps = {
  error: unknown;
  resetError?: () => void;
  componentStack?: string;
  // Set false when an outer Sentry ErrorBoundary already captures the error.
  report?: boolean;
};

// One recovery attempt per pathname per window; a persistent crash reports.
const DOM_RECOVERY_WINDOW_MS = 10_000;
const lastDomRecoveryAt = new Map<string, number>();

export const ErrorCatch = ({
  error,
  componentStack,
  resetError,
  report = true,
}: ErrorCatchProps) => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isChunkError = error instanceof Error && isChunkMissingError(error);
  const willRecoverFromDomError =
    !!resetError &&
    error instanceof Error &&
    isThirdPartyDomMutationError(error) &&
    Date.now() - (lastDomRecoveryAt.get(pathname) ?? 0) >
      DOM_RECOVERY_WINDOW_MS;

  // Chunk-missing errors are benign (new version deployed); NewVersionModal handles them.
  useEffect(() => {
    if (willRecoverFromDomError) {
      lastDomRecoveryAt.set(pathname, Date.now());
      resetError?.();
      return;
    }
    if (!report || isChunkError) return;
    // No custom fingerprint: default (stack) grouping gives one issue per
    // distinct bug; the route stays queryable via the transaction/pathname tags.
    IssueReporter.error("Route crashed", {
      error,
      source: "client",
      pathname,
      componentStack,
      // A crash, not a caught error — report it as unhandled so it reaches the
      // route-crash alert (filters on error.unhandled) and the crash-free rate.
      handled: false,
    });
  }, [
    error,
    componentStack,
    pathname,
    isChunkError,
    report,
    willRecoverFromDomError,
    resetError,
  ]);

  // A blank frame while the boundary resets beats flashing the error page.
  if (willRecoverFromDomError) {
    return null;
  }

  if (isChunkError) {
    return (
      <>
        <NewVersionModal />
        <ErrorPage />
      </>
    );
  }
  return <ErrorPage />;
};
